import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Mic, RefreshCcw, UserRound } from 'lucide-react';
import { apiService } from '../../services/api';
import DeleteMeetingButton from './DeleteMeetingButton';
import { useMeetingRecorder } from './MeetingRecorderContext';
import { formatDuration, formatMeetingDate, statusOf } from './meetingStatus';
import { ScopeChip } from './ScopePicker';
import { NexusReviewBadge } from './TacticalMeetingRecap';

const POLL_MS = 15000;
const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;

// Une tactique de mes cercles : autre forme qu'une réunion enregistrée (tour des projets, ordre du jour).
const TacticalRow = ({ meeting }) => (
  <li className="border-l-4 border-indigo-400 hover:bg-indigo-50/40">
    <Link to={`/meetings/tactique/${meeting.id}`} className="flex min-w-0 flex-wrap items-center gap-3 px-4 py-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
        <RefreshCcw className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">
          Réunion tactique{meeting.circle_name ? <span className="font-normal text-slate-500"> · {meeting.circle_name}</span> : null}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
          <span>{formatMeetingDate(meeting.started_at)}</span>
          {meeting.duration_seconds ? <span>· {formatDuration(meeting.duration_seconds)}</span> : null}
          {meeting.projects > 0 && <span>· {plural(meeting.projects, 'projet')}</span>}
          {meeting.points > 0 && <span>· {plural(meeting.points, 'point')}</span>}
          {meeting.actions > 0 && <span>· {plural(meeting.actions, 'tâche')}</span>}
          {!meeting.attended && (
            <span className="flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-violet-700">
              <UserRound className="h-3 w-3" /> absent
            </span>
          )}
        </p>
      </div>
      <NexusReviewBadge state={meeting.nexus_review} compact />
    </Link>
  </li>
);

// Mes réunions : celles que j'ai enregistrées ou auxquelles j'ai participé, avec ou sans projet, et les tactiques
// de mes cercles. Une réunion sans projet est liée aux personnes présentes : elle n'apparaît qu'ici (et pour ses
// participants).
const MyMeetings = () => {
  const recorder = useMeetingRecorder();
  const [meetings, setMeetings] = useState(null);
  const [tacticals, setTacticals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);

  const load = useCallback(() => apiService.get('/meetings?mine=true&limit=100')
    .then((data) => { setMeetings(data.items); setError(null); })
    .catch((err) => setError(err.message)), []);
  useEffect(() => { load(); }, [load, recorder.phase]);
  useEffect(() => {
    apiService.get('/circle-meetings/mine?limit=60')
      .then((items) => setTacticals(Array.isArray(items) ? items : []))
      .catch(() => setTacticals([])); // l'historique des tactiques ne doit jamais bloquer la liste
  }, []);

  const busy = (meetings || []).some((m) => statusOf(m.status).busy);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, load]);

  const personal = (meetings || []).filter((m) => !m.scope).length;
  const shown = useMemo(() => {
    const recorded = (meetings || []).filter((m) => filter === 'all' || (filter === 'personal' && !m.scope));
    const tactical = filter === 'all' || filter === 'tactical' ? tacticals.map((m) => ({ ...m, tactical: true })) : [];
    const time = (meeting) => new Date(meeting.started_at || 0).getTime() || 0;
    return [...recorded, ...tactical].sort((a, b) => time(b) - time(a));
  }, [meetings, tacticals, filter]);
  const empty = {
    all: "Aucune réunion pour l'instant.",
    personal: 'Aucune réunion sans projet.',
    tactical: 'Aucune réunion tactique close dans tes cercles.',
  }[filter];

  return (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Mes réunions</h1>
          <p className="text-sm text-slate-500">Celles que tu as enregistrées ou auxquelles tu as participé, et les tactiques de tes cercles.</p>
        </div>
        <button type="button" onClick={() => recorder.openDialog()}
          className="flex items-center gap-1.5 rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700">
          <Mic className="h-4 w-4" /> Enregistrer une réunion
        </button>
      </div>
      <div className="flex flex-wrap gap-1 text-sm">
        {[['all', 'Toutes'], ['personal', `Sans projet${personal ? ` · ${personal}` : ''}`],
          ['tactical', `Tactiques${tacticals.length ? ` · ${tacticals.length}` : ''}`]].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setFilter(key)}
            className={`rounded-lg px-3 py-1 font-medium ${filter === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-white'}`}>
            {label}
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!meetings ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : shown.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400">{empty}</p>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
          {shown.map((meeting) => {
            if (meeting.tactical) return <TacticalRow key={`tactique-${meeting.id}`} meeting={meeting} />;
            const status = statusOf(meeting.status);
            return (
              <li key={meeting.id} className="flex items-center gap-1 pr-3 hover:bg-slate-50">
                <Link to={`/meetings/${meeting.id}`} className="flex min-w-0 flex-1 flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{meeting.title}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                      <span>{formatMeetingDate(meeting.started_at)}</span>
                      {meeting.duration_seconds ? <span>· {formatDuration(meeting.duration_seconds)}</span> : null}
                      {meeting.project_id ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{meeting.project_name}</span>
                      ) : meeting.scope ? (
                        <ScopeChip meeting={meeting} />
                      ) : (
                        <span className="flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-violet-700">
                          <UserRound className="h-3 w-3" /> Sans projet
                        </span>
                      )}
                      {!meeting.can_read_transcript && <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> transcript réservé</span>}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${status.tone}`}>{status.label}</span>
                </Link>
                <DeleteMeetingButton meeting={meeting} onDeleted={(id) => setMeetings((prev) => prev.filter((m) => m.id !== id))} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MyMeetings;
